import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin } from "lucide-react";
import { SEOHead, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AreasPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/locations`)
      .then(r => setLocations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const regions = [...new Set(locations.map(l => l.region))];

  return (
    <main className="pt-16" data-testid="areas-page">
      <SEOHead
        title="Web Design by Location — Melbourne Suburbs & Regional Victoria"
        description="Professional website design for Melbourne suburbs and regional Victoria. We build websites for businesses in Bayside, St Kilda, Richmond, Fitzroy, Brunswick, Geelong, and more."
        path="/areas"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Areas", path: "/areas" }])}
      />
      <Breadcrumbs items={[{ name: "Areas We Serve", path: "/areas" }]} />

      <section className="bg-[#0055FF] text-white border-b-4 border-black section-padding">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest text-[#FFD600] mb-4">Local Web Design</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Web Design for<br />Melbourne & Victoria
          </h1>
          <p className="text-lg text-blue-200 font-medium max-w-2xl mx-auto">
            We build professional websites for businesses across Melbourne's suburbs and regional Victoria. Find your area below.
          </p>
        </div>
      </section>

      <section className="section-padding" data-testid="areas-grid">
        <div className="container-main">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-black border-t-[#FFD600] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-xl font-bold">Loading areas...</p>
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-20" data-testid="areas-empty">
              <MapPin size={40} className="text-[#0055FF] mx-auto mb-4" />
              <p className="text-2xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>We serve businesses Australia-wide</p>
              <p className="text-gray-600 font-medium">Get in touch and we'll build a professional website for your business, wherever you are.</p>
            </div>
          ) : (
            <>
              {regions.map(region => (
                <div key={region} className="mb-12">
                  <div className="flex items-center gap-2 mb-6">
                    <MapPin size={20} className="text-[#0055FF]" />
                    <h2 className="text-xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{region}</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {locations.filter(l => l.region === region).map((loc, i) => (
                      <Link
                        key={loc.id || i}
                        to={`/areas/${loc.slug}`}
                        className="group bg-white border-2 border-black rounded-2xl p-6 flex flex-col transition-transform hover:-translate-y-2 duration-200"
                        style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                        data-testid={`area-card-${loc.slug}`}
                      >
                        <h3 className="text-lg font-black mb-2 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {loc.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-4">{loc.excerpt}</p>
                        <span className="font-bold text-[#0055FF] flex items-center gap-1 text-sm">
                          Learn More <ArrowRight size={14} />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      <section className="section-padding bg-[#F4F4F5] border-t-4 border-black">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Don't See Your Area?
          </h2>
          <p className="text-lg font-medium text-gray-600 mb-8 max-w-xl mx-auto">
            We build websites for businesses across all of Australia. Our service isn't limited to Melbourne — we work remotely with clients everywhere.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="areas-contact-cta">
              Get a Free Quote <ArrowRight size={16} />
            </Link>
            <Link to="/packages" className="btn-secondary inline-flex items-center gap-2" data-testid="areas-packages-cta">
              View Packages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#FFD600] border-t-4 border-black">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Professional Websites from $499
          </h2>
          <p className="text-lg font-semibold mb-8">Same great service, wherever you are in Australia.</p>
          <Link to="/purchase" className="btn-secondary inline-flex items-center gap-2 text-lg" data-testid="areas-buy-cta">
            Get Started Now <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
